import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
    PAGE_SIZE_OPTIONS,
    PATIENT_SEARCH_COLUMNS,
    PATIENT_SORT_FIELDS,
} from './subparts/patientFilesQuery';
import PatientFilesIcon from './subparts/PatientFilesIcon';
import { PATIENT_FILES_PAGE_THEME } from './subparts/patientFilesListTheme';

function PatientSearchForm2({
    onSearch,
    onShowAll,
    isLoading,
    searchTerm: externalSearchTerm,
    searchColumn: externalSearchColumn,
    sortBy,
    sortOrder,
    pageSize,
    onSortByChange,
    onSortOrderChange,
    onPageSizeChange,
}) {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const { register, handleSubmit, watch, setValue } = useForm({
        defaultValues: {
            searchTerm: externalSearchTerm || '',
            searchColumn: externalSearchColumn || 'firstName',
        },
    });

    const searchTerm = watch('searchTerm', '');
    const searchColumn = watch('searchColumn', 'firstName');
    const selectedColumn = PATIENT_SEARCH_COLUMNS.find((option) => option.value === searchColumn);
    const searchPlaceholder = selectedColumn
        ? `Search by ${selectedColumn.label.toLowerCase()}...`
        : 'Search patients...';

    const onSubmit = (data) => onSearch(data.searchColumn, data.searchTerm);

    const handleClear = () => {
        setValue('searchTerm', '');
        onSearch(searchColumn, '');
    };

    const handleColumnChange = (column) => {
        setValue('searchColumn', column);
        if (searchTerm.trim()) {
            onSearch(column, searchTerm);
        }
    };

    return (
        <div className="patient-files-filters">
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="patient-files-filters__row">
                    <div className="patient-files-filters__search input-group input-group-sm">
                        <span className="input-group-text patient-files-filters__search-by-label">
                            Search By
                        </span>
                        <select
                            className="form-select form-select-sm patient-files-filters__search-by"
                            aria-label="Search By"
                            {...register('searchColumn')}
                            onChange={(event) => handleColumnChange(event.target.value)}
                            disabled={isLoading}
                        >
                            {PATIENT_SEARCH_COLUMNS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <span className="input-group-text patient-files-filters__search-icon bg-white border-start-0 border-end-0">
                            <PatientFilesIcon icon="bi-search" theme={PATIENT_FILES_PAGE_THEME} size="xs" />
                        </span>
                        <input
                            type="text"
                            className="form-control form-control-sm border-start-0"
                            placeholder={searchPlaceholder}
                            {...register('searchTerm')}
                            disabled={isLoading}
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                className="btn btn-outline-secondary patient-files-filters__clear-btn"
                                onClick={handleClear}
                                disabled={isLoading}
                                aria-label="Clear search"
                            >
                                <i className="bi bi-x-lg" />
                            </button>
                        )}
                        <button
                            type="submit"
                            className="btn btn-light-primary patient-files-filters__search-btn"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" />
                                    Searching
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-search me-1" />
                                    Search
                                </>
                            )}
                        </button>
                    </div>

                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary patient-files-filters__show-all-btn"
                        onClick={onShowAll}
                        disabled={isLoading}
                    >
                        <i className="bi bi-people me-1" />
                        Show All
                    </button>

                    <button
                        type="button"
                        className={`btn btn-sm patient-files-filters__toggle${
                            filtersOpen ? ' btn-light-primary is-open' : ' btn-outline-secondary'
                        }`}
                        onClick={() => setFiltersOpen((open) => !open)}
                        aria-expanded={filtersOpen}
                        aria-controls="patient-files-advanced-filters"
                    >
                        <i className="bi bi-sliders me-1" aria-hidden="true" />
                        Filters
                        <i
                            className={`bi bi-chevron-${filtersOpen ? 'up' : 'down'} ms-1 patient-files-filters__toggle-chevron`}
                            aria-hidden="true"
                        />
                    </button>
                </div>
            </form>

            {filtersOpen && (
                <div
                    id="patient-files-advanced-filters"
                    className="patient-files-filters__advanced"
                >
                    <label className="patient-files-filters__control mb-0">
                        <span className="patient-files-filters__control-label">Sort</span>
                        <select
                            className="form-select form-select-sm"
                            value={sortBy}
                            onChange={(event) => onSortByChange?.(event.target.value)}
                            disabled={isLoading}
                            aria-label="Sort by"
                        >
                            {PATIENT_SORT_FIELDS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="patient-files-filters__control mb-0">
                        <span className="patient-files-filters__control-label">Order</span>
                        <select
                            className="form-select form-select-sm patient-files-filters__order"
                            value={sortOrder}
                            onChange={(event) => onSortOrderChange?.(event.target.value)}
                            disabled={isLoading}
                            aria-label="Sort order"
                        >
                            <option value="ASC">Asc</option>
                            <option value="DESC">Desc</option>
                        </select>
                    </label>

                    <label className="patient-files-filters__control mb-0">
                        <span className="patient-files-filters__control-label">Show</span>
                        <select
                            className="form-select form-select-sm patient-files-filters__page-size"
                            value={pageSize}
                            onChange={(event) => onPageSizeChange?.(Number(event.target.value))}
                            disabled={isLoading}
                            aria-label="Entries per page"
                        >
                            {PAGE_SIZE_OPTIONS.map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                        <span className="patient-files-filters__control-suffix">entries</span>
                    </label>
                </div>
            )}
        </div>
    );
}

export default PatientSearchForm2;
